import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { ArrowUpDown } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { CanProject } from "@/app/capabilities/components/can-project";
import { PROJECT } from "@/app/capabilities/permissions";
import { ListLayout } from "@/components/custom/list-layout/list-layout";
import { useGetPlugins, useGetProjectHooksIfReadable } from "./api/hooks";
import { ConnectedSystemsSection } from "./components/connected-systems/connected-systems-section";
import { HookExecutionPipeline } from "./components/hook-execution-pipeline/hook-execution-pipeline";
import { ReorderHooksModal } from "./components/reorder-hooks-modal/reorder-hooks-modal";
import { ViewPluginModal } from "./components/view-plugin-modal/view-plugin-modal";
import type { Plugin } from "./types";
import { enrichHooksWithPlugins, groupPluginsByBinding } from "./utils";

export const LogicExtensionsList = () => {
  // Fetch all plugins and hooks visible at the project level
  // This includes both project-level and inherited tenant-level resources
  const { data: allPlugins = [] } = useGetPlugins();
  const allHooks = useGetProjectHooksIfReadable();

  // Track which plugin is being hovered in the table
  const [hoveredPluginId, setHoveredPluginId] = useState<string | null>(null);

  // Track selected plugin for view modal
  const [selectedPlugin, setSelectedPlugin] = useState<Plugin | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  // Track reorder modal state
  const [isReorderModalOpen, setIsReorderModalOpen] = useState(false);

  // Enrich hooks with plugin data for the pipeline visualization
  const enrichedHooks = useMemo(
    () => enrichHooksWithPlugins(allHooks, allPlugins),
    [allHooks, allPlugins]
  );

  // Group plugins by binding
  const pluginsByBinding = useMemo(
    () => groupPluginsByBinding(allPlugins),
    [allPlugins]
  );

  // Handle plugin selection (from table or pipeline)
  const handleSelectPlugin = useCallback(
    (pluginId: string) => {
      const plugin = allPlugins.find((p) => p.id === pluginId);
      if (plugin) {
        setSelectedPlugin(plugin);
        setIsViewModalOpen(true);
      }
    },
    [allPlugins]
  );

  return (
    <ListLayout
      description="Govern what tools users use, verify requests are safe, enrich tool calls with data, and protect sensitive outputs."
      title="Contextual Access"
    >
      <div className="flex flex-col gap-8">
        <div className="rounded-lg border bg-card">
          <div className="flex items-center justify-between border-b px-6 py-4">
            <div className="flex items-center gap-3">
              <span className="font-medium text-sm">Pipeline</span>
            </div>
            {enrichedHooks.length > 0 && (
              <CanProject permission={PROJECT.hooks.update}>
                <Button
                  onClick={() => setIsReorderModalOpen(true)}
                  size="sm"
                  variant="outline"
                >
                  <ArrowUpDown className="mr-2 size-4" />
                  Reorder Hooks
                </Button>
              </CanProject>
            )}
          </div>
          <HookExecutionPipeline
            highlightedPluginId={hoveredPluginId}
            hooks={enrichedHooks}
            onSelectPlugin={handleSelectPlugin}
          />
        </div>

        <p className="text-center text-muted-foreground text-xs">
          Hover a system below to highlight it in the pipeline.
        </p>

        {/* Connected Systems Section */}
        <ConnectedSystemsSection
          hooks={allHooks}
          onHoverPlugin={setHoveredPluginId}
          onSelectPlugin={handleSelectPlugin}
          pluginsByBinding={pluginsByBinding}
        />
      </div>

      {/* View Plugin Modal */}
      <ViewPluginModal
        hooks={allHooks}
        onOpenChange={setIsViewModalOpen}
        open={isViewModalOpen}
        plugin={selectedPlugin}
      />

      {/* Reorder Hooks Modal */}
      <ReorderHooksModal
        onOpenChange={setIsReorderModalOpen}
        open={isReorderModalOpen}
      />
    </ListLayout>
  );
};
